import { brand } from '../../lib/brand';
import { useState, useCallback, useMemo } from 'react';

// ============================================================
// Types
// ============================================================
interface SubModule {
  id: string;
  name: string;
  items: number;
  desc: string;
  status: 'done' | 'partial' | 'pending';
  /** 對應的前端頁面路徑（如有） */
  route?: string;
  /** 對應的後端模組（如有） */
  apiModule?: string;
}

interface Module {
  id: string;
  name: string;
  phase: number;
  color: string;
  status: 'done' | 'partial' | 'pending';
  subModules: SubModule[];
}

interface Dependency {
  from: string;
  to: string;
  label: string;
}

interface PhaseInfoEntry {
  name: string;
  color: string;
  border: string;
  status: 'done' | 'partial' | 'pending';
  completedAt?: string;
}

// ============================================================
// Data
// ============================================================
const MODULES: Module[] = [
  {
    id: 'M1',
    name: '§1 認證與權限系統',
    phase: 1,
    color: '#ef4444',
    status: 'done',
    subModules: [
      {
        id: '1-1',
        name: '系統初始化與場館建置',
        items: 6,
        status: 'done',
        desc: 'Seed Admin、初始化精靈、場館資訊、最高管理員建立、預設角色匯入',
        route: '/admin/users',
        apiModule: 'auth/',
      },
      {
        id: '1-2',
        name: '帳號管理與安全驗證',
        items: 7,
        status: 'done',
        desc: 'JWT (15m) + Refresh Token (7d HttpOnly Cookie)、TOTP 2FA (otplib)、帳號 CRUD、登入失敗鎖定',
        route: '/login',
        apiModule: 'auth/',
      },
      {
        id: '1-3',
        name: '角色與細粒度權限 (RBAC)',
        items: 7,
        status: 'done',
        desc: '角色 CRUD、頁面/操作級權限矩陣、JwtAuthGuard、即時生效',
        route: '/admin/roles',
        apiModule: 'role/',
      },
    ],
  },
  {
    id: 'M2',
    name: '§2 錦標賽核心引擎',
    phase: 3,
    color: '#f59e0b',
    status: 'done',
    subModules: [
      {
        id: '2-1',
        name: '錦標賽模板系統',
        items: 8,
        status: 'done',
        desc: '系統預設 10 種模板、自訂模板 CRUD（建立/編輯/刪除）、從模板套用建賽、模板管理獨立頁面',
        route: '/tournament/templates',
        apiModule: 'tournament/',
      },
      {
        id: '2-2',
        name: '獎金結構與發放計算',
        items: 15,
        status: 'done',
        desc: 'ICM Malmuth-Harville 演算法（POST /icm/calculate）、標準/自訂比例、GTD Overlay 警示、Bounty 追蹤、簽收單列印',
        route: '/tournament/:id/payout',
        apiModule: 'tournament/',
      },
      {
        id: '2-3',
        name: '賽事建立與設定',
        items: 6,
        status: 'done',
        desc: '建立賽事（選模板+盲注結構）、Rebuy/Addon 規則設定、延遲報名 Level、賽事詳情中樞頁（狀態依據顯示不同操作）',
        route: '/tournament/create → /tournament/:id',
        apiModule: 'tournament/',
      },
      {
        id: '2-4',
        name: '盲注結構設定',
        items: 5,
        status: 'done',
        desc: '盲注結構 CRUD（獨立編輯器頁面）、BB Ante、Timed/DeepStack/BBA 四種模式、等級新增/編輯/刪除、結構預覽',
        route: '/tournament/blind-structures',
        apiModule: 'tournament/',
      },
      {
        id: '2-5',
        name: '計時器控制與 WebSocket',
        items: 6,
        status: 'done',
        desc: 'REST API：pause/resume/complete（更新 tournament status）、WS：start/next_level/prev_level/set_time、timer_state JSONB 持久化、每秒 tick 自動推進、到時自動升級、Socket.IO /tournament Gateway 雙向同步',
        route: '/tournament/:id/timer',
        apiModule: 'tournament/',
      },
      {
        id: '2-5A',
        name: '電視計時器同步',
        items: 4,
        status: 'done',
        desc: 'broadcastToDisplay 即時同步至 /display namespace、Active/Break/Registration 三種模式自動切換、管理員更新螢幕綁定時通知 TV 客戶端切換 Room',
        route: '/display/screen/:token',
        apiModule: 'display/',
      },
      {
        id: '2-6',
        name: '淘汰 / Rebuy / Addon 記錄',
        items: 5,
        status: 'done',
        desc: '淘汰記錄（POST /eliminate + 排名指定）、Rebuy/Addon（POST /rebuy-addon）、次數上限驗證、Addon 視窗、規則引擎驗證、30 秒撤銷（荷官台）',
        route: '/tournament/:id/eliminate',
        apiModule: 'tournament/',
      },
      {
        id: '2-6A',
        name: '玩家報名與報到',
        items: 5,
        status: 'done',
        desc: '會員搜尋報名（POST /entries）、匿名報名、確認報到（POST /entries/:entryId/check-in）、取消報名（DELETE /entries/:entryId）、報名完成自動返回',
        route: '/tournament/:id/register',
        apiModule: 'tournament/',
      },
      {
        id: '2-7',
        name: '獎金池自動計算',
        items: 3,
        status: 'done',
        desc: '報名/Rebuy/Addon 即時累計、GTD Overlay 自動計算、WS 廣播 prize_pool:updated、同步至電視螢幕',
        apiModule: 'tournament/',
      },
      {
        id: '2-8',
        name: '多場錦標賽管理',
        items: 5,
        status: 'done',
        desc: '賽事列表（分頁/狀態篩選/refetchOnMount）、賽事詳情中樞、狀態機 DRAFT→REGISTERED→IN_PROGRESS⇄PAUSED→COMPLETED、start/pause/resume/complete/cancel 端點',
        route: '/tournament → /tournament/:id',
        apiModule: 'tournament/',
      },
      {
        id: '2-9',
        name: '過往錦標賽存取',
        items: 7,
        status: 'done',
        desc: '歷史列表（COMPLETED/CANCELLED 篩選）、詳情回顧、淘汰/Rebuy 歷史記錄（GET /eliminations、/rebuy-addons）',
        route: '/tournament',
        apiModule: 'tournament/',
      },
      {
        id: '2-10',
        name: '賽事故障排除與恢復',
        items: 8,
        status: 'done',
        desc: 'timer_state JSONB 持久化、computeCurrentState 計算時間差續算、伺服器重啟自動恢復、WebSocket 斷線重連指數退避 (100ms→10s)、useCountdown 本地倒數同步',
        apiModule: 'tournament/',
      },
    ],
  },
  {
    id: 'M3',
    name: '§3 會員與報到系統',
    phase: 2,
    color: '#22c55e',
    status: 'done',
    subModules: [
      {
        id: '3-1',
        name: '會員資料庫設計',
        items: 0,
        status: 'done',
        desc: 'members 表、延展欄位 Custom Fields、member_reward_points_log、member_prize_credit_log',
        apiModule: 'member/',
      },
      {
        id: '3-1A',
        name: '獎勵點數規則',
        items: 0,
        status: 'done',
        desc: '等級倍率換算、月結歸零、計點來源、reward_points_config',
        route: '/admin/points-config',
        apiModule: 'member/',
      },
      {
        id: '3-1B',
        name: '獎金點數規則',
        items: 0,
        status: 'done',
        desc: '正值/負值機制、帳務連動、日結流程',
        apiModule: 'member/',
      },
      {
        id: '3-2',
        name: '會員管理介面',
        items: 17,
        status: 'done',
        desc: '列表分頁/搜尋/CRUD、狀態管理、等級調整、點數管理、稽核日誌',
        route: '/members',
        apiModule: 'member/',
      },
      {
        id: '3-3',
        name: '批次匯入 (Excel/Sheets)',
        items: 9,
        status: 'done',
        desc: 'XLSX 拖曳上傳、react-dropzone、欄位對應 Column Mapping、格式驗證、重複偵測',
        route: '/members/import',
        apiModule: 'member/',
      },
      {
        id: '3-4',
        name: '快速報到與賽事報名',
        items: 6,
        status: 'done',
        desc: '模糊搜尋 300ms Debounce、欠款/黑名單警示、臨時建檔、POS 收據列印',
        route: '/check-in',
        apiModule: 'member/',
      },
      {
        id: '3-5',
        name: '第三方系統串接 (預留)',
        items: 8,
        status: 'pending',
        desc: 'Adapter Pattern、連線設定、單/雙向同步（MVP 後實作）',
      },
    ],
  },
  {
    id: 'M4',
    name: '§4 基礎帳務',
    phase: 2,
    color: '#3b82f6',
    status: 'done',
    subModules: [
      {
        id: '4-1',
        name: '帳務資料結構設計',
        items: 0,
        status: 'done',
        desc: 'daily_accounting_sessions、accounting_transactions、venue_expense_categories',
        apiModule: 'accounting/',
      },
      {
        id: '4-2',
        name: '每日開帳',
        items: 5,
        status: 'done',
        desc: '前日結餘帶入、備用金登記、開帳鎖定防重複',
        route: '/accounting/open',
        apiModule: 'accounting/',
      },
      {
        id: '4-3',
        name: '賽事收費自動記錄',
        items: 6,
        status: 'done',
        desc: '報到入帳、Rebuy/Addon 入帳、獎金出帳（Phase 3 接入）',
        apiModule: 'accounting/',
      },
      {
        id: '4-4',
        name: '館務費用收支管理',
        items: 6,
        status: 'done',
        desc: '費用類別 CRUD、單筆登記、收入/支出分類',
        route: '/accounting/expenses',
        apiModule: 'accounting/',
      },
      {
        id: '4-5',
        name: '日結算與報表發送',
        items: 12,
        status: 'done',
        desc: '日結觸發、收支匯總、現金清點、window.print() A4 PDF、Email 設定',
        route: '/accounting/report',
        apiModule: 'accounting/',
      },
    ],
  },
  {
    id: 'M5',
    name: '§5 電視顯示系統',
    phase: 4,
    color: '#a855f7',
    status: 'done',
    subModules: [
      {
        id: '5-1',
        name: '後台電視顯示管理',
        items: 6,
        status: 'done',
        desc: 'display_screens DB、Token URL 生成、QR Code、賽事指派',
        route: '/display',
        apiModule: 'display/',
      },
      {
        id: '5-2',
        name: '全螢幕計時器主畫面',
        items: 9,
        status: 'done',
        desc: '倒數計時器 JetBrains Mono tabular-nums、盲注顯示、休息/報名模式、Token URL 無需登入',
        route: '/display/screen/:token',
        apiModule: 'display/',
      },
      {
        id: '5-3',
        name: '盲注結構表顯示',
        items: 4,
        status: 'done',
        desc: '完整等級表、當前等級高亮、已過淡化',
        route: '/display/screen/:token',
      },
      {
        id: '5-4',
        name: '獎金結構表顯示',
        items: 3,
        status: 'done',
        desc: '各名次獎金、ICM 試算、獎金池即時顯示',
        route: '/display/screen/:token',
      },
      {
        id: '5-5',
        name: '遙控器操作介面',
        items: 7,
        status: 'partial',
        desc: '方向鍵導航、賽事/模式切換選單（基礎實作，部分互動待完善）',
        route: '/display/screen/:token',
      },
      {
        id: '5-6',
        name: '解析度適配與效能',
        items: 8,
        status: 'done',
        desc: 'vw/vh 縮放、1080p~4K 支援、60fps 渲染',
        route: '/display/screen/:token',
      },
    ],
  },
  {
    id: 'M6',
    name: '§6 場管與荷官操作介面',
    phase: 4,
    color: '#ec4899',
    status: 'done',
    subModules: [
      {
        id: '6-1',
        name: '場館牌桌設置',
        items: 5,
        status: 'done',
        desc: 'venue_tables CRUD、樓層分組、賽事指派、座位分配 player_seat_assignments',
        apiModule: 'venue/',
      },
      {
        id: '6-2',
        name: '場管操作台',
        items: 8,
        status: 'done',
        desc: '全場桌次總覽、計時器控制、桌次平衡提示、Socket.IO 即時同步',
        route: '/floor/:tournamentId',
        apiModule: 'venue/',
      },
      {
        id: '6-3',
        name: '荷官操作台',
        items: 7,
        status: 'done',
        desc: '指派桌次、淘汰/Rebuy/Addon 按鈕、30 秒誤操作撤銷 (undo stack)',
        route: '/dealer/:tournamentId/:tableId',
        apiModule: 'tournament/',
      },
      {
        id: '6-4',
        name: '橫式顯示與牌桌模式',
        items: 7,
        status: 'done',
        desc: "CSS @media orientation + JS screen.orientation.lock('landscape')、觸控按鈕 44px 熱區",
        route: '/dealer/:tournamentId/:tableId',
      },
      {
        id: '6-5',
        name: '換桌與拆桌功能',
        items: 7,
        status: 'done',
        desc: '單人換桌、拆桌流程、抽籤分配、player_table_transfers 歷史記錄',
        apiModule: 'venue/',
      },
      {
        id: '6-6',
        name: '跨桌資訊合併結算',
        items: 5,
        status: 'done',
        desc: 'player_entry 主鍵跨桌追蹤、換桌歷史、ICM 結算接入',
        apiModule: 'venue/',
      },
      {
        id: '6-7',
        name: 'PWA 基礎支援',
        items: 3,
        status: 'done',
        desc: 'vite-plugin-pwa、Web App Manifest landscape orientation、Service Worker 離線緩衝',
      },
    ],
  },
  {
    id: 'M7',
    name: '§7 系統日誌與稽核',
    phase: 1,
    color: '#64748b',
    status: 'done',
    subModules: [
      {
        id: '7-1',
        name: '日誌分類與資料結構',
        items: 0,
        status: 'done',
        desc: '6 等級 (DEBUG~CRITICAL) × 8 分類 (AUTH/MEMBER/...) + JSONB 快照、system_logs (BIGSERIAL)',
        apiModule: 'log/',
      },
      {
        id: '7-2',
        name: '操作稽核日誌',
        items: 4,
        status: 'done',
        desc: 'Before/After 快照、actorId/IP 記錄、LogService.audit()',
        apiModule: 'log/',
      },
      {
        id: '7-3',
        name: '系統事件日誌',
        items: 6,
        status: 'done',
        desc: '計時器事件、伺服器生命週期、WebSocket 連線/斷線',
        apiModule: 'log/',
      },
      {
        id: '7-4',
        name: '錯誤與安全稽核',
        items: 6,
        status: 'done',
        desc: 'API 錯誤捕捉、認證失敗 + 帳號鎖定警示、未授權存取記錄',
        apiModule: 'log/',
      },
      {
        id: '7-5',
        name: '日誌查詢與管理',
        items: 8,
        status: 'done',
        desc: '分頁/篩選查詢、等級/分類/日期範圍、匯出、保留政策',
        route: '/admin/logs',
        apiModule: 'log/',
      },
    ],
  },
  {
    id: 'M8',
    name: '§8 多語言與術語設定',
    phase: 2,
    color: '#06b6d4',
    status: 'done',
    subModules: [
      {
        id: '8-1',
        name: '多語言架構設計',
        items: 0,
        status: 'done',
        desc: 'react-i18next + i18next-http-backend、zh-TW/zh-CN/en、DB 覆蓋層（優先級：DB > JSON > fallback）',
        apiModule: 'i18n/',
      },
      {
        id: '8-2',
        name: '語言切換功能',
        items: 6,
        status: 'done',
        desc: 'LanguageSwitcher 元件、localStorage 記憶偏好、6 個命名空間',
        route: '/admin/terminology',
      },
      {
        id: '8-3',
        name: '術語對照表 (場館自訂)',
        items: 8,
        status: 'done',
        desc: 'i18n_custom_terms DB 表、自訂術語 CRUD、JSON 匯出/匯入、即時全站生效',
        route: '/admin/terminology',
        apiModule: 'i18n/',
      },
    ],
  },
  {
    id: 'M9',
    name: '§Phase 5 上線準備',
    phase: 5,
    color: '#10b981',
    status: 'partial',
    subModules: [
      {
        id: '9-1',
        name: 'Docker 容器化部署',
        items: 6,
        status: 'done',
        desc: 'docker-compose.yml（6 容器）、多階段 Dockerfile × 3、nginx 反向代理 + WS upgrade、.env.example',
      },
      {
        id: '9-2',
        name: '2FA Setup 前端設定頁',
        items: 3,
        status: 'done',
        desc: 'Setup2FAPage：POST /auth/2fa/setup → QR Code → TOTP 驗證 → 備用碼顯示，路由 /auth/setup-2fa',
        route: '/auth/setup-2fa',
        apiModule: 'auth/',
      },
      {
        id: '9-3',
        name: 'PWA Service Worker + Icons',
        items: 4,
        status: 'done',
        desc: 'vite-plugin-pwa 加入 web app、SVG Icons（favicon/192/512）、Workbox 離線緩衝（tournament API NetworkFirst）',
      },
      {
        id: '9-4',
        name: '列印功能 (POS + A4)',
        items: 5,
        status: 'done',
        desc: '@media print CSS（POS 58mm + A4）、PosReceipt 可重用元件、window.print() 觸發、PrintReportPage A4 日結報表',
        route: '/accounting/report',
      },
      {
        id: '9-5',
        name: 'Playwright E2E 測試',
        items: 6,
        status: 'done',
        desc: 'apps/e2e/ 獨立套件、playwright.config.ts、global setup/teardown、6 個 spec 檔（smoke + 5 旅程）',
      },
      {
        id: '9-6',
        name: '部署文件',
        items: 2,
        status: 'done',
        desc: 'DEPLOY.md（Mac Mini M1 + Zeabur VPS 2vCore/4GB 完整步驟）、SystemDesign/PROGRESS.md 更新',
      },
      {
        id: '9-7',
        name: 'OWASP 安全掃描',
        items: 3,
        status: 'pending',
        desc: 'OWASP ZAP Top 10 掃描（需線上環境）、待 Staging 環境就緒後執行',
      },
    ],
  },
];

const DEPENDENCIES: Dependency[] = [
  // Phase 1 → Phase 2
  { from: '1-2', to: '3-4', label: 'JWT 驗證' },
  { from: '1-3', to: '3-2', label: 'RBAC 控管' },
  { from: '1-1', to: '6-1', label: '初始化精靈' },
  // Phase 2 internal
  { from: '3-1', to: '3-2', label: 'DB Schema' },
  { from: '3-1A', to: '3-2', label: '點數規則' },
  { from: '3-1B', to: '3-2', label: '獎金點數' },
  { from: '3-1', to: '3-4', label: '會員資料' },
  { from: '4-1', to: '4-2', label: '帳務結構' },
  { from: '4-2', to: '4-5', label: '開帳→日結' },
  { from: '4-4', to: '4-5', label: '費用→日結' },
  { from: '8-1', to: '8-2', label: 'i18n 架構' },
  { from: '8-1', to: '8-3', label: 'i18n 架構' },
  // Phase 2 → Phase 3
  { from: '3-4', to: '4-3', label: '報到觸發收費' },
  { from: '3-4', to: '2-3', label: '報名需會員' },
  // Phase 3 main chain
  { from: '2-1', to: '2-3', label: '模板→建賽' },
  { from: '2-3', to: '2-4', label: '賽事→盲注' },
  { from: '2-4', to: '2-5', label: '盲注→計時器' },
  { from: '2-5', to: '2-5A', label: '計時器→電視同步' },
  { from: '2-5', to: '2-6', label: '計時器→操作' },
  { from: '2-6', to: '2-7', label: '操作→獎金池' },
  { from: '2-7', to: '2-2', label: '獎金池→發放' },
  { from: '3-4', to: '2-6A', label: '報到→賽事報名' },
  { from: '2-6A', to: '2-3', label: '報名→賽事詳情' },
  { from: '2-6A', to: '2-7', label: '報名→獎金池累計' },
  // Phase 3 parallel
  { from: '2-3', to: '2-8', label: '賽事→多場管理' },
  { from: '2-3', to: '2-9', label: '賽事→歷史' },
  { from: '2-5', to: '2-10', label: '計時器→恢復' },
  { from: '2-3', to: '4-3', label: '賽事→自動記帳' },
  // Phase 3 → Phase 4
  { from: '2-5A', to: '5-2', label: '電視同步→顯示' },
  { from: '2-5', to: '5-2', label: '計時器→電視' },
  { from: '2-4', to: '5-3', label: '盲注→電視' },
  { from: '2-2', to: '5-4', label: '獎金→電視' },
  { from: '5-1', to: '5-2', label: 'TV Manager' },
  { from: '5-1', to: '5-3', label: 'TV Manager' },
  { from: '5-1', to: '5-4', label: 'TV Manager' },
  { from: '2-3', to: '6-2', label: '賽事→場管台' },
  { from: '2-6', to: '6-3', label: '操作→荷官台' },
  { from: '6-1', to: '6-2', label: '牌桌→場管' },
  { from: '6-1', to: '6-3', label: '牌桌→荷官' },
  { from: '6-2', to: '6-5', label: '場管→換桌' },
  { from: '6-5', to: '6-6', label: '換桌→跨桌結算' },
  { from: '2-2', to: '6-6', label: '獎金→跨桌結算' },
  // Cross-cutting logs
  { from: '7-1', to: '7-2', label: 'Schema' },
  { from: '7-1', to: '7-3', label: 'Schema' },
  { from: '7-1', to: '7-4', label: 'Schema' },
  { from: '7-2', to: '7-5', label: '稽核→查詢' },
  // Phase 4 → Phase 5
  { from: '1-2', to: '9-2', label: '2FA 後端→前端' },
  { from: '6-7', to: '9-3', label: 'PWA 基礎→SW' },
  { from: '4-5', to: '9-4', label: '日結→列印' },
  { from: '3-4', to: '9-4', label: '報到→POS 收據' },
  { from: '9-1', to: '9-6', label: 'Docker→文件' },
  { from: '9-5', to: '9-7', label: 'E2E→OWASP' },
];

const PHASE_INFO: Record<number, PhaseInfoEntry> = {
  1: {
    name: 'Phase 1 — 基礎架構',
    color: '#fee2e2',
    border: '#ef4444',
    status: 'done',
    completedAt: '2026-03-04',
  },
  2: {
    name: 'Phase 2 — 資料基礎層',
    color: '#dcfce7',
    border: '#22c55e',
    status: 'done',
    completedAt: '2026-03-05',
  },
  3: {
    name: 'Phase 3 — 錦標賽核心引擎',
    color: '#fef3c7',
    border: '#f59e0b',
    status: 'done',
    completedAt: '2026-03-05',
  },
  4: {
    name: 'Phase 4 — 前端顯示與操作',
    color: '#f3e8ff',
    border: '#a855f7',
    status: 'done',
    completedAt: '2026-03-05',
  },
  5: {
    name: 'Phase 5 — 上線準備',
    color: '#d1fae5',
    border: '#10b981',
    status: 'partial',
    completedAt: '2026-03-05',
  },
};

const STATUS_BADGE: Record<
  'done' | 'partial' | 'pending',
  { label: string; bg: string; text: string }
> = {
  done: { label: '✓ 完成', bg: '#14532d', text: '#86efac' },
  partial: { label: '◐ 部分', bg: '#713f12', text: '#fcd34d' },
  pending: { label: '○ 待開發', bg: '#1e1b4b', text: '#a5b4fc' },
};

// ============================================================
// Prop types
// ============================================================
interface OverviewViewProps {
  modules: Module[];
  selectedModule: string | null;
  setSelectedModule: (id: string | null) => void;
  getRelatedDeps: (subId: string) => Dependency[];
}

interface DependencyViewProps {
  modules: Module[];
  selectedPhase: number | null;
  phaseModules: Record<number, Module[]>;
}

interface ProgressViewProps {
  modules: Module[];
  phaseModules: Record<number, Module[]>;
}

// ============================================================
// Main component
// ============================================================
export default function UserStoryMapPage() {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState('overview');

  const phaseModules = useMemo(() => {
    const map: Record<number, Module[]> = {};
    MODULES.forEach((m) => {
      if (!map[m.phase]) map[m.phase] = [];
      map[m.phase]!.push(m);
    });
    return map;
  }, []);

  const getRelatedDeps = useCallback(
    (subId: string): Dependency[] => DEPENDENCIES.filter((d) => d.from === subId || d.to === subId),
    [],
  );

  const filteredModules = selectedPhase
    ? MODULES.filter((m) => m.phase === selectedPhase)
    : MODULES;

  const totalItems = MODULES.flatMap((m) => m.subModules).reduce((s, sub) => s + sub.items, 0);
  const doneItems = MODULES.flatMap((m) => m.subModules)
    .filter((sub) => sub.status === 'done')
    .reduce((s, sub) => s + sub.items, 0);

  return (
    <div
      className="min-h-screen bg-gray-950 text-white p-4"
      style={{ fontFamily: "'Noto Sans TC', 'Segoe UI', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {brand.name} — User Story 總覽與關聯圖
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">
              MVP 核心功能共 <span className="text-yellow-400 font-bold">{totalItems}</span> 項 /{' '}
              <span className="text-yellow-400 font-bold">8</span> 大模組 /{' '}
              <span className="text-yellow-400 font-bold">5</span> 個開發階段
            </p>
          </div>
          {/* 整體進度 */}
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-1">整體完成度</div>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${Math.round((doneItems / totalItems) * 100)}%` }}
                />
              </div>
              <span className="text-emerald-400 text-sm font-bold">
                {Math.round((doneItems / totalItems) * 100)}%
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 mb-3">
          {(['overview', 'dependency', 'progress'] as const).map((mode) => {
            const labels: Record<string, string> = {
              overview: '模組總覽',
              dependency: '依賴關係',
              progress: '完成進度',
            };
            return (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className="btn-sm px-3 py-1.5 rounded text-sm font-medium transition"
                style={{
                  background: viewMode === mode ? '#2563eb' : '#1f2937',
                  color: viewMode === mode ? '#fff' : '#9ca3af',
                }}
              >
                {labels[mode]}
              </button>
            );
          })}
          <div className="border-l border-gray-700 mx-1" />
          <button
            onClick={() => setSelectedPhase(null)}
            className="btn-sm px-3 py-1.5 rounded text-sm transition"
            style={{
              background: !selectedPhase ? '#4b5563' : '#1f2937',
              color: !selectedPhase ? '#fff' : '#9ca3af',
            }}
          >
            全部
          </button>
          {[1, 2, 3, 4, 5].map((p) => (
            <button
              key={p}
              onClick={() => setSelectedPhase(selectedPhase === p ? null : p)}
              className="btn-sm px-3 py-1.5 rounded text-sm font-medium transition flex items-center gap-1"
              style={{
                background: selectedPhase === p ? PHASE_INFO[p]!.border : '#1f2937',
                color: selectedPhase === p ? 'white' : PHASE_INFO[p]!.border,
                border: `1px solid ${PHASE_INFO[p]!.border}`,
              }}
            >
              P{p}
              {PHASE_INFO[p]!.status === 'done' && <span className="text-[9px] opacity-80">✓</span>}
            </button>
          ))}
        </div>

        {/* Phase Legend */}
        <div className="flex flex-wrap gap-3 mb-5 text-xs text-gray-400">
          {Object.entries(PHASE_INFO).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ background: v.border }} />
              <span>{v.name}</span>
              {v.status === 'done' ? (
                <span className="text-emerald-500">✓</span>
              ) : v.status === 'partial' ? (
                <span className="text-yellow-500">◐</span>
              ) : null}
              {v.completedAt && <span className="text-gray-600">{v.completedAt}</span>}
            </div>
          ))}
        </div>

        {viewMode === 'overview' && (
          <OverviewView
            modules={filteredModules}
            selectedModule={selectedModule}
            setSelectedModule={setSelectedModule}
            getRelatedDeps={getRelatedDeps}
          />
        )}
        {viewMode === 'dependency' && (
          <DependencyView
            modules={MODULES}
            selectedPhase={selectedPhase}
            phaseModules={phaseModules}
          />
        )}
        {viewMode === 'progress' && (
          <ProgressView modules={filteredModules} phaseModules={phaseModules} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Overview View
// ============================================================
function OverviewView({
  modules,
  selectedModule,
  setSelectedModule,
  getRelatedDeps,
}: OverviewViewProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {modules.map((mod) => {
        const badge = STATUS_BADGE[mod.status];
        return (
          <div
            key={mod.id}
            className="rounded-lg border transition-all cursor-pointer"
            style={{
              borderColor: selectedModule === mod.id ? mod.color : '#374151',
              background: selectedModule === mod.id ? `${mod.color}10` : '#111827',
            }}
            onClick={() => setSelectedModule(selectedModule === mod.id ? null : mod.id)}
          >
            {/* Module Header */}
            <div className="p-3 border-b" style={{ borderColor: '#374151' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: mod.color }}
                  />
                  <span className="font-bold text-sm">{mod.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                    style={{ background: badge.bg, color: badge.text }}
                  >
                    {badge.label}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      background: PHASE_INFO[mod.phase]!.border + '30',
                      color: PHASE_INFO[mod.phase]!.border,
                    }}
                  >
                    P{mod.phase}
                  </span>
                  <span className="text-xs text-gray-400">
                    {mod.subModules.reduce((s, sub) => s + sub.items, 0)} 項
                  </span>
                </div>
              </div>
            </div>

            {/* Sub Modules */}
            <div className="p-2">
              {mod.subModules.map((sub) => {
                const deps = getRelatedDeps(sub.id);
                const subBadge = STATUS_BADGE[sub.status];
                return (
                  <div key={sub.id} className="p-2 rounded mb-1 hover:bg-gray-800 transition">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-medium text-white">
                        <span className="text-gray-500 mr-1">{sub.id}</span>
                        {sub.name}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span
                          className="text-[9px] px-1 py-0.5 rounded"
                          style={{ background: subBadge.bg, color: subBadge.text }}
                        >
                          {subBadge.label}
                        </span>
                        {sub.items > 0 && (
                          <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">
                            {sub.items}
                          </span>
                        )}
                        {deps.length > 0 && (
                          <span className="text-[10px] bg-blue-900 text-blue-300 px-1.5 py-0.5 rounded">
                            {deps.length} 關聯
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedModule === mod.id && (
                      <p className="text-[11px] text-gray-500 mt-1">{sub.desc}</p>
                    )}
                    {selectedModule === mod.id && sub.route && (
                      <p className="text-[10px] text-blue-400/60 mt-0.5">
                        路由：{sub.route}
                        {sub.apiModule && (
                          <span className="ml-2 text-purple-400/60">
                            API：modules/{sub.apiModule}
                          </span>
                        )}
                      </p>
                    )}
                    {selectedModule === mod.id && deps.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {deps.map((d, i) => (
                          <span
                            key={i}
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: d.from === sub.id ? '#1e3a5f' : '#3a1e1e',
                              color: d.from === sub.id ? '#93c5fd' : '#fca5a5',
                            }}
                          >
                            {d.from === sub.id ? `→ ${d.to}` : `← ${d.from}`} {d.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Dependency View
// ============================================================
type SubModuleWithMeta = SubModule & {
  moduleId: string;
  moduleName: string;
  color: string;
  phase: number;
};

function DependencyView({ modules, selectedPhase, phaseModules }: DependencyViewProps) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const allSubModules = useMemo(() => {
    const map: Record<string, SubModuleWithMeta> = {};
    modules.forEach((m) => {
      m.subModules.forEach((sub) => {
        map[sub.id] = {
          ...sub,
          moduleId: m.id,
          moduleName: m.name,
          color: m.color,
          phase: m.phase,
        };
      });
    });
    return map;
  }, [modules]);

  const filteredDeps = useMemo(() => {
    if (!selectedPhase) return DEPENDENCIES;
    return DEPENDENCIES.filter((d) => {
      const from = allSubModules[d.from];
      const to = allSubModules[d.to];
      return from && to && (from.phase === selectedPhase || to.phase === selectedPhase);
    });
  }, [selectedPhase, allSubModules]);

  const hoveredDeps = useMemo(() => {
    if (!hoveredNode) return [];
    return DEPENDENCIES.filter((d) => d.from === hoveredNode || d.to === hoveredNode);
  }, [hoveredNode]);

  const hoveredConnected = useMemo(() => {
    if (!hoveredNode) return new Set<string>();
    const set = new Set<string>([hoveredNode]);
    hoveredDeps.forEach((d) => {
      set.add(d.from);
      set.add(d.to);
    });
    return set;
  }, [hoveredNode, hoveredDeps]);

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        共 {filteredDeps.length} 條依賴關係。將滑鼠移至節點上可查看關聯。
      </p>

      {[1, 2, 3, 4, 5]
        .filter((p) => !selectedPhase || p === selectedPhase)
        .map((phase) => {
          const mods = phaseModules[phase] ?? [];
          if (mods.length === 0) return null;
          return (
            <div
              key={phase}
              className="mb-4 rounded-lg p-3 border"
              style={{
                borderColor: PHASE_INFO[phase]!.border + '40',
                background: PHASE_INFO[phase]!.color + '08',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-bold" style={{ color: PHASE_INFO[phase]!.border }}>
                  {PHASE_INFO[phase]!.name}
                </h3>
                {PHASE_INFO[phase]!.status === 'done' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900 text-emerald-400">
                    ✓ 完成 {PHASE_INFO[phase]!.completedAt}
                  </span>
                )}
                {PHASE_INFO[phase]!.status === 'partial' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900 text-yellow-400">
                    ◐ 部分完成
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {mods.map((mod) => (
                  <div key={mod.id} className="flex-1 min-w-[240px]">
                    <div className="text-xs font-bold mb-1.5" style={{ color: mod.color }}>
                      {mod.name}
                    </div>
                    <div className="space-y-1">
                      {mod.subModules.map((sub) => {
                        const isHovered = hoveredNode === sub.id;
                        const isConnected = hoveredConnected.has(sub.id);
                        const dimmed = hoveredNode && !isConnected;
                        const subBadge = STATUS_BADGE[sub.status];
                        return (
                          <div
                            key={sub.id}
                            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all cursor-default"
                            style={{
                              background: isHovered
                                ? mod.color + '30'
                                : isConnected
                                  ? mod.color + '15'
                                  : '#1f2937',
                              borderLeft: `3px solid ${isHovered ? mod.color : isConnected ? mod.color + '80' : 'transparent'}`,
                              opacity: dimmed ? 0.25 : 1,
                            }}
                            onMouseEnter={() => setHoveredNode(sub.id)}
                            onMouseLeave={() => setHoveredNode(null)}
                          >
                            <span className="text-gray-500 w-8 flex-shrink-0 text-[10px]">
                              {sub.id}
                            </span>
                            <span className="text-gray-200 truncate flex-1">{sub.name}</span>
                            <span
                              className="text-[8px] px-1 rounded flex-shrink-0"
                              style={{ background: subBadge.bg, color: subBadge.text }}
                            >
                              {sub.status === 'done' ? '✓' : sub.status === 'partial' ? '◐' : '○'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

      {/* Hovered tooltip */}
      {hoveredNode && (
        <div className="fixed bottom-4 right-4 bg-gray-900 border border-gray-700 rounded-lg p-3 max-w-sm shadow-2xl z-50">
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: allSubModules[hoveredNode]?.color }}
            />
            <span className="font-bold text-sm">
              {hoveredNode} — {allSubModules[hoveredNode]?.name}
            </span>
          </div>
          {hoveredDeps.length > 0 ? (
            <div className="space-y-1">
              {hoveredDeps.map((d, i) => (
                <div key={i} className="text-xs flex items-center gap-1">
                  {d.from === hoveredNode ? (
                    <>
                      <span className="text-blue-400">→</span>
                      <span className="text-gray-400">{d.to}</span>
                      <span className="text-gray-600">({d.label})</span>
                    </>
                  ) : (
                    <>
                      <span className="text-red-400">←</span>
                      <span className="text-gray-400">{d.from}</span>
                      <span className="text-gray-600">({d.label})</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">無依賴關係</p>
          )}
        </div>
      )}

      {/* Dependency list */}
      <div className="mt-4 rounded-lg border border-gray-800 bg-gray-900 p-3">
        <h3 className="text-sm font-bold text-gray-300 mb-2">
          依賴關係列表（{filteredDeps.length} 條）
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {filteredDeps.map((dep, i) => {
            const from = allSubModules[dep.from];
            const to = allSubModules[dep.to];
            if (!from || !to) return null;
            return (
              <div
                key={i}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-gray-800"
              >
                <span style={{ color: from.color }}>{dep.from}</span>
                <span className="text-gray-600">→</span>
                <span style={{ color: to.color }}>{dep.to}</span>
                <span className="text-gray-600 ml-1">{dep.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Progress View
// ============================================================
function ProgressView({ modules, phaseModules }: ProgressViewProps) {
  const phases = [1, 2, 3, 4, 5];

  return (
    <div className="space-y-4">
      {/* Phase summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-2">
        {phases.map((p) => {
          const pMods = phaseModules[p] ?? [];
          const allSubs = pMods.flatMap((m) => m.subModules);
          const doneSubs = allSubs.filter((s) => s.status === 'done').length;
          const info = PHASE_INFO[p]!;
          const pct = allSubs.length > 0 ? Math.round((doneSubs / allSubs.length) * 100) : 0;
          return (
            <div
              key={p}
              className="rounded-lg p-3 border"
              style={{ borderColor: info.border + '50', background: info.color + '10' }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold" style={{ color: info.border }}>
                  Phase {p}
                </span>
                <span className="text-[10px] font-bold" style={{ color: info.border }}>
                  {pct}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden mb-1">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: info.border }}
                />
              </div>
              <div className="text-[10px] text-gray-500">
                {doneSubs}/{allSubs.length} 子模組
              </div>
              {info.completedAt && info.status === 'done' && (
                <div className="text-[10px] text-emerald-500 mt-0.5">✓ {info.completedAt}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Module-level progress */}
      {modules.map((mod) => {
        const doneSubs = mod.subModules.filter((s) => s.status === 'done').length;
        const totalSubs = mod.subModules.length;
        const pct = Math.round((doneSubs / totalSubs) * 100);
        const badge = STATUS_BADGE[mod.status];

        return (
          <div
            key={mod.id}
            className="rounded-lg border bg-gray-900"
            style={{ borderColor: mod.color + '30' }}
          >
            {/* Module header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: mod.color }} />
                <span className="font-semibold text-sm text-white">{mod.name}</span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: badge.bg, color: badge.text }}
                >
                  {badge.label}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: mod.color }}
                    />
                  </div>
                  <span className="text-xs font-bold" style={{ color: mod.color }}>
                    {pct}%
                  </span>
                </div>
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    background: PHASE_INFO[mod.phase]!.border + '20',
                    color: PHASE_INFO[mod.phase]!.border,
                  }}
                >
                  P{mod.phase}
                </span>
              </div>
            </div>

            {/* Sub-module rows */}
            <div className="divide-y divide-gray-800">
              {mod.subModules.map((sub) => {
                const subBadge = STATUS_BADGE[sub.status];
                return (
                  <div key={sub.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                    <span className="text-gray-600 w-8 flex-shrink-0">{sub.id}</span>
                    <span className="flex-1 text-gray-300 truncate" title={sub.name}>
                      {sub.name}
                    </span>
                    {sub.route && (
                      <span className="text-blue-500/50 text-[10px] hidden md:block truncate max-w-[140px]">
                        {sub.route}
                      </span>
                    )}
                    {sub.apiModule && (
                      <span className="text-purple-500/50 text-[10px] hidden lg:block truncate max-w-[100px]">
                        {sub.apiModule}
                      </span>
                    )}
                    {sub.items > 0 && (
                      <span className="text-gray-600 flex-shrink-0">{sub.items} 項</span>
                    )}
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: subBadge.bg, color: subBadge.text }}
                    >
                      {subBadge.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
